import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { useMutation, useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { format, isFuture } from 'date-fns';
import React, { useEffect, useState } from 'react';
import Button from '@dashboard/components/Button';
import Faq from '@dashboard/components/Faq';
import { Label, Switch, FormSubmit } from '@dashboard/components/Form';
import { FormErrorBase } from '@dashboard/components/Formik/components/FormError';
import Link from '@dashboard/components/Link';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { formatPrice } from '@dashboard/services/formatting';
import plans, { getBillingPeriod, getPlan } from '@dashboard/services/plans';
import { fetcher } from '@dashboard/services/request';
import { useToast } from '@dashboard/context/ToastContext';
import SettingsContainer from '@dashboard/components/SettingsContainer';
import SettingsHeader from '@dashboard/components/SettingsHeader';
import useUrlQuery from '@dashboard/hooks/useUrlQuery';
import { useHistory } from 'react-router-dom';
import ScrollToTop from '@/shared/components/ScrollToTop';
import { sellerKey } from '@dashboard/lib/queryKeys';
import Callout from '@dashboard/components/Callout';
import { stringify } from 'query-string';
import getCouponDiscount from './getCouponDiscount';
import useRewardful from '@dashboard/hooks/useRewardful';
import scaleBrackets from '@dashboard/services/scaleBrackets';

const availablePlans = plans.filter(plan => plan.available);

const Plan = ({
  handle,
  version,
  billingPeriod = 'month',
  discount,
  selected,
  setSelectedPlanHandle,
  isCurrentPlan,
  selectedScaleBracket,
  setSelectedScaleBracket,
}) => {
  const plan = getPlan(handle, version);

  if (!plan) return null;

  // Combine Grow and Scale plans like UpgradePage.js
  const isGrowScalePlan = handle === 'grow' || handle === 'scale';

  const scaleBracket = isGrowScalePlan
    ? scaleBrackets.find(b => {
        return b.value === selectedScaleBracket;
      })
    : null;

  // Override plan prices for Grow/Scale plan based on selected bracket
  const displayPlan =
    isGrowScalePlan && scaleBracket
      ? {
          ...plan,
          title: selectedScaleBracket <= 10000 ? 'Grow' : 'Scale',
          monthlyPrice: scaleBracket.monthlyPrice,
          yearlyPrice: scaleBracket.yearlyPrice,
          benefits: [scaleBracket.label],
        }
      : plan;

  return (
    <button
      className={clsx('rounded-lg border-2 w-full text-left p-4', {
        'border-gray-200': !selected,
        'border-orange-500': selected,
        'bg-gray-50 border-transparent': isCurrentPlan,
      })}
      onClick={() => setSelectedPlanHandle(plan.handle)}
      disabled={isCurrentPlan}
    >
      <div className="flex justify-between flex-start">
        <div className="text-left flex-1">
          <div className="flex justify-between gap-x-2">
            <div className="font-bold mb-1">
              {displayPlan.title}

              {isCurrentPlan && billingPeriod === 'year' ? ' yearly' : ''}

              {displayPlan.available ? (
                ''
              ) : (
                <span className="ml-1 text-gray-700 font-normal">
                  {'('}legacy{')'}
                </span>
              )}
            </div>

            {isCurrentPlan && <PlanStatus plan={plan} isCurrentPlan={isCurrentPlan} />}
          </div>

          <div className="text-gray-700 font-medium mb-1 whitespace-nowrap">
            {displayPlan.handle === 'enterprise' && (
              <span className="text-black font-bold">Custom</span>
            )}

            {displayPlan.handle !== 'enterprise' && (
              <>
                {billingPeriod === 'month' && (
                  <>
                    <span className="text-black font-bold">
                      {isCurrentPlan && discount
                        ? `${formatPrice({
                            amount:
                              displayPlan.monthlyPrice -
                              getCouponDiscount(displayPlan.monthlyPrice, {
                                amountOff: discount.amountOff,
                                percentOff: discount.percentOff,
                              }),
                            currency: 'USD',
                          })} / month`
                        : `${formatPrice({
                            amount: displayPlan.monthlyPrice,
                            currency: 'USD',
                          })} / month`}
                    </span>{' '}
                  </>
                )}

                {billingPeriod === 'year' && (
                  <>
                    <span className="text-black font-bold">
                      <span className="line-through text-gray-600 mr-2">
                        {formatPrice({
                          amount: displayPlan.monthlyPrice,
                          currency: 'USD',
                        })}{' '}
                        / month
                      </span>

                      {isCurrentPlan && discount ? (
                        <>
                          {formatPrice({
                            amount:
                              displayPlan.yearlyPrice -
                              getCouponDiscount(displayPlan.yearlyPrice, {
                                amountOff: discount.amountOff,
                                percentOff: discount.percentOff,
                              }),
                            currency: 'USD',
                          })}{' '}
                          / month
                        </>
                      ) : (
                        <>
                          {formatPrice({
                            amount: displayPlan.yearlyPrice,
                            currency: 'USD',
                          })}{' '}
                          / month
                        </>
                      )}
                    </span>
                  </>
                )}
              </>
            )}
          </div>

          {isGrowScalePlan && !isCurrentPlan ? (
            <div className="text-gray-700 font-medium flex gap-x-2 items-center">
              <div className="relative w-full max-w-xs">
                <select
                  className="w-full mt-1 border h-9 py-1 px-2 rounded-md outline-chocolate-600 border-gray-300 pr-8 appearance-none text-sm"
                  value={selectedScaleBracket}
                  onChange={e => {
                    e.stopPropagation();
                    setSelectedScaleBracket(Number(e.target.value));
                    // Set the appropriate plan handle based on bracket value
                    const newPlanHandle = Number(e.target.value) <= 10000 ? 'grow' : 'scale';
                    setSelectedPlanHandle(newPlanHandle);
                  }}
                  onClick={e => {
                    e.stopPropagation();
                    // Set the appropriate plan handle based on current bracket value
                    const currentPlanHandle = selectedScaleBracket <= 10000 ? 'grow' : 'scale';
                    setSelectedPlanHandle(currentPlanHandle);
                  }}
                >
                  {scaleBrackets.map(bracket => (
                    <option key={bracket.value} value={bracket.value}>
                      {bracket.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          ) : (
            <div className="text-gray-700 font-medium">
              {displayPlan.benefits.map((benefit, index) => (
                <span key={index}>
                  {benefit}
                  {index < displayPlan.benefits.length - 1 ? <span className="mx-1">•</span> : ''}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </button>
  );
};

const PlanOverview = () => {
  const history = useHistory();
  const {
    plan: sellerPlan,
    seller,
    isCanceled,
    isTrialing,
    isCanceling,
    isTrialExpired,
    token,
    sellerId,
  } = useAuth();
  const urlQuery = useUrlQuery();
  const {
    billing_period,
    plan: planQuery,
    coupon: couponQuery,
    scaleBracket: scaleBracketQuery,
  } = urlQuery;

  const [selectedBillingPeriod, setSelectedBillingPeriod] = useState(
    billing_period || seller.planBillingPeriod || 'month',
  );

  const [selectedPlanHandle, setSelectedPlanHandle] = React.useState(
    planQuery
      ? planQuery
      : isTrialing || isCanceling || isCanceled || isTrialExpired
        ? 'grow'
        : null,
  );

  const [selectedScaleBracket, setSelectedScaleBracket] = useState(
    scaleBracketQuery ? Number(scaleBracketQuery) : sellerPlan.bracket || 10000,
  );

  useEffect(() => {
    if (sellerPlan.bracket) {
      setSelectedScaleBracket(sellerPlan.bracket);
      return;
    }

    if (sellerPlan.handle === 'grow') {
      setSelectedScaleBracket(10000);
      return;
    }

    if (sellerPlan.handle === 'scale') {
      setSelectedScaleBracket(30000);
      return;
    }
  }, [sellerPlan.bracket, sellerPlan.handle]);

  const { coupon: rewardfulCoupon } = useRewardful();

  const { data: coupon } = useQuery({
    queryKey: ['coupon', couponQuery, rewardfulCoupon, sellerId],
    queryFn: () =>
      fetcher(
        `/sellers/${sellerId}/validate-coupon?coupon=${couponQuery || rewardfulCoupon.id}`,
        token,
      ),
    enabled: !!sellerId && (!!couponQuery || !!rewardfulCoupon),
    retry: false,
  });

  const currentPlan = getPlan(sellerPlan.handle, sellerPlan.version) || {};
  const selectedPlan = getPlan(selectedPlanHandle);

  useEffect(() => {
    if (planQuery) {
      setSelectedPlanHandle(planQuery);
    }
  }, [planQuery]);

  useEffect(() => {
    if (!seller._id) return;

    if (selectedPlanHandle) return;

    if (planQuery) return;

    if (seller.subscriptionStatus === 'legacy') return;

    const preselectPlan = availablePlans.find(
      plan => plan.handle === currentPlan.handle && plan.version === currentPlan.version,
    );

    if (preselectPlan) {
      setSelectedPlanHandle(preselectPlan.handle);
    }
  }, [seller, currentPlan]);

  // set billing period from sellerPlan
  useEffect(() => {
    if (billing_period) return;

    if (seller.planBillingPeriod) {
      setSelectedBillingPeriod(seller.planBillingPeriod);

      history.push({
        search: stringify({
          ...urlQuery,
          billing_period: seller.planBillingPeriod,
        }),
      });
    }
  }, [seller]);

  // set coupon from sellerPlan
  useEffect(() => {
    if (couponQuery) return;

    if (seller.stripeSubscriptionDiscount) {
      history.push({
        search: stringify({
          ...urlQuery,
          coupon: seller.stripeSubscriptionDiscount.promotionCode,
        }),
      });
    }
  }, [seller]);

  function toggleBillingPeriod() {
    setSelectedBillingPeriod(selectedBillingPeriod === 'month' ? 'year' : 'month');

    history.push({
      search: stringify({
        ...urlQuery,
        billing_period: selectedBillingPeriod === 'month' ? 'year' : 'month',
      }),
    });
  }

  const hasSubscription = seller.subscriptionStatus === 'active';

  const sellerLosesCustomDomain =
    seller.customDomain && seller.customDomainVerified && selectedPlanHandle === 'launch';

  const monthlyCouponDiscount = getCouponDiscount(selectedPlan?.monthlyPrice, {
    amountOff: coupon?.amount_off,
    percentOff: coupon?.percent_off,
  });

  const yearlyCouponDiscount = getCouponDiscount(selectedPlan?.yearlyPrice * 12, {
    amountOff: coupon?.amount_off,
    percentOff: coupon?.percent_off,
  });

  const discountedMonthlyPrice = selectedPlan?.monthlyPrice - monthlyCouponDiscount;

  const yearlyPrice = selectedPlan?.yearlyPrice * 12;
  const discountedYearlyPrice = yearlyPrice - yearlyCouponDiscount;

  return (
    <>
      <ScrollToTop />

      <SettingsContainer size="2xl">
        {currentPlan.handle && (
          <div>
            <div>
              {hasSubscription && (
                <div className="mb-8">
                  <SettingsHeader>Current plan</SettingsHeader>

                  <Plan
                    handle={currentPlan.handle === 'scale' ? 'grow' : currentPlan.handle}
                    version={currentPlan.version}
                    billingPeriod={seller.planBillingPeriod}
                    discount={seller.stripeSubscriptionDiscount}
                    setSelectedPlanHandle={() => null}
                    isCurrentPlan
                    selectedScaleBracket={
                      sellerPlan.bracket || (sellerPlan.handle === 'grow' ? 10000 : 30000)
                    }
                  />
                </div>
              )}

              {!isTrialing && !hasSubscription && (
                <div className="mb-8">
                  <PlanStatus plan={currentPlan} isCurrentPlan={true} />
                </div>
              )}

              <div className="">
                <SettingsHeader>
                  {isTrialing || isTrialExpired
                    ? 'Start subscription'
                    : seller.subscriptionStatus === 'active'
                      ? 'Change plan'
                      : 'Upgrade plan'}
                </SettingsHeader>

                <div className="flex justify-between mt-3 mb-3">
                  <div>
                    <Link target="blank" to="/pricing">
                      Compare pricing plans{' '}
                      <InformationCircleIcon className="inline-block w-4 h-4" />
                    </Link>
                  </div>

                  <div className="flex items-center">
                    <Switch
                      onChange={toggleBillingPeriod}
                      value={selectedBillingPeriod === 'year'}
                      id="billingPeriod"
                    />
                    <Label
                      className="ml-2 cursor-pointer !mb-0 !text-base font-medium !text-gray-900"
                      htmlFor="billingPeriod"
                    >
                      Yearly billing (2 months free)
                    </Label>
                  </div>
                </div>

                <div className="space-y-3">
                  {availablePlans
                    .filter(plan => {
                      if (isTrialing) return true;
                      if (isCanceling) return true;

                      if (selectedBillingPeriod !== seller.planBillingPeriod) return true;

                      return true;
                    })
                    .filter(plan => plan.handle !== 'scale') // Filter out scale plan since it's combined with grow
                    .map(plan => (
                      <Plan
                        key={plan.handle}
                        handle={plan.handle}
                        version={plan.version}
                        billingPeriod={selectedBillingPeriod}
                        selected={
                          selectedPlan &&
                          (selectedPlan.handle === plan.handle ||
                            (plan.handle === 'grow' && selectedPlan.handle === 'scale'))
                        }
                        setSelectedPlanHandle={setSelectedPlanHandle}
                        selectedScaleBracket={selectedScaleBracket}
                        setSelectedScaleBracket={setSelectedScaleBracket}
                      />
                    ))}
                </div>

                {sellerLosesCustomDomain && (
                  <div className="mt-4">
                    <Callout variant="warning">
                      You’ll lose your custom domain. Subscribe to Grow or Scale to keep your custom
                      domain.
                    </Callout>
                  </div>
                )}

                {coupon && (
                  <div className="flex justify-end mt-2">
                    <div className="bg-orange-50 px-4 py-4 space-y-1 text-right rounded">
                      {rewardfulCoupon && (
                        <p>
                          <span className="font-semibold">{rewardfulCoupon.name}</span>
                        </p>
                      )}

                      <p>
                        <span className="font-semibold">Discount</span>:{' '}
                        {formatPrice({
                          amount:
                            selectedBillingPeriod === 'year'
                              ? yearlyCouponDiscount
                              : monthlyCouponDiscount,
                          currency: 'USD',
                        })}
                        <>
                          {coupon.duration === 'once' && ' (one-time)'}

                          {coupon.duration === 'forever' && ` / ${selectedBillingPeriod}`}

                          {coupon.duration === 'repeating' &&
                            ` (${coupon.duration_in_months} months)`}
                        </>
                      </p>

                      <p>
                        <span className="font-semibold">Total</span>:{' '}
                        {formatPrice({
                          amount:
                            selectedBillingPeriod === 'year'
                              ? discountedYearlyPrice
                              : discountedMonthlyPrice,
                          currency: 'USD',
                        })}{' '}
                        / {selectedBillingPeriod}
                      </p>
                    </div>
                  </div>
                )}

                <div className="mt-4">
                  {selectedPlan && (
                    <SubscriptionButton
                      plan={
                        selectedPlan.handle === 'grow' && selectedScaleBracket > 10000
                          ? getPlan('scale')
                          : selectedPlan
                      }
                      billingPeriod={selectedBillingPeriod}
                      setSelectedPlanHandle={setSelectedPlanHandle}
                      upgradePath="/billing/plan/upgrade"
                      upgradeFlow="billing-settings"
                      selectedScaleBracket={selectedScaleBracket}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </SettingsContainer>

      <div className="mt-16">
        <SettingsContainer size="2xl">
          <SettingsHeader>Frequently asked questions</SettingsHeader>

          <div className="mx-auto max-w-2xl">
            <Faq>
              <Faq.Item
                question="What happens when my 7-day free trial ends?"
                answer="To use Checkout Page, you need to have an active subscription. If you decide not to subscribe before your trial ends, your pages will no longer work and you'll lose access to your dashboard."
              />

              <Faq.Item
                question="Are there any limits to the 7-day free trial?"
                answer="During your 7-day free trial, you can process up to $500 in payments. If you exceed this limit, you'll need to subscribe to one of our paid plans to continue accepting payments.
                <br /><br />
                To prevent interruptions to your payments, please start a subscription during your trial if you expect to exceed this limit."
              />

              <Faq.Item
                question="What happens if I exceed the sales per month limit?"
                answer={
                  <>
                    <p className="mb-4">
                      If you exceed the sales per month limit, you will be charged an overage fee.
                    </p>
                    <p className="mb-4">
                      You can upgrade your plan at any time to increase your sales per month limit.
                    </p>
                    <p>
                      For detailed information about overages,{' '}
                      <Link href="/help/articles/pricing-fees" target="blank" variant="secondary">
                        visit our pricing guide
                      </Link>
                      .
                    </p>
                  </>
                }
              />

              <Faq.Item
                question="If I don’t like Checkout Page, can I request a refund?"
                answer={
                  <>
                    <p className="mb-4">
                      We'd hate to see you go, but we totally understand if Checkout Page is not the
                      right fit for you.
                    </p>
                    <p className="mb-4">
                      If you're not satisfied, we offer a 7-day money-back guarantee for new
                      customers on both monthly and annual plans.
                    </p>
                    <p className="mb-4">
                      You’re eligible for a full refund if you contact us within 7 days of starting
                      your subscription, and you haven’t processed any revenue through your account.
                    </p>
                    <p className="mb-4">
                      Refer to our{' '}
                      <Link href="/help/articles/refund-policy" target="blank" variant="secondary">
                        refund policy
                      </Link>{' '}
                      for more information.
                    </p>
                  </>
                }
              />

              <Faq.Item
                question="How do I change my plan?"
                answer="You can change your plan anytime. Upgrades take effect immediately and you'll be charged the difference between the old and new plan. Downgrades take effect at the end of the current billing period."
              />

              <Faq.Item
                question="How do I change my billing period?"
                answer={
                  <>
                    <div className="mb-4">You can change your billing period at any time.</div>
                    <div className="mb-4">
                      When you change from monthly to yearly billing, the change goes into effect
                      immediately.
                    </div>
                    <div className="mb-4">
                      Please contact us to change from yearly to monthly billing. This change goes
                      into effect at the start of the next billing period.
                    </div>
                  </>
                }
              />
            </Faq>
          </div>
        </SettingsContainer>
      </div>

      {hasSubscription && !seller.stripeSubscriptionCancelAt && (
        <div className="mt-16">
          <SettingsContainer size="2xl">
            <SettingsHeader>Cancel subscription</SettingsHeader>
            <div className="mb-4">
              You can cancel your subscription anytime by clicking "Cancel subscription". When you
              cancel your subscription, you can use Checkout Page until the end of your billing
              period.
            </div>
            <div className="flex justify-between mt-1.5">
              <Button appearance="secondary" to="/billing/plan/cancel">
                Cancel subscription
              </Button>
            </div>
          </SettingsContainer>
        </div>
      )}
    </>
  );
};

function PlanStatus({ isCurrentPlan }) {
  const { isTrialing, seller } = useAuth();

  const hasActiveSubscription = seller.subscriptionStatus === 'active';

  if (!hasActiveSubscription || isTrialing) {
    return null;
  }

  if (isCurrentPlan) {
    return <div className="text-gray-700 font-medium">{getRenewalStatus(seller)}</div>;
  }

  return null;
}

function getRenewalStatus(seller) {
  const trialEndsAt = seller.trialEnd || null;
  const renewsAt = seller.stripeSubscriptionCurrentPeriodEnd || null;
  const cancelsAt = seller.stripeSubscriptionCancelAt || null;
  const hasPendingPlanChange = seller.pendingPlanChange?.stripeSubscriptionScheduleId;

  // pending plan changes are always downgrades
  if (hasPendingPlanChange) {
    const pendingPlan = seller.pendingPlanChange.plan;
    if (pendingPlan.handle === 'scale' && pendingPlan.bracket) {
      return `Downgrades to ${pendingPlan.title} ($${pendingPlan.bracket / 1000}K) on ${format(
        new Date(seller.pendingPlanChange.effectiveDate),
        'd MMM, yyyy',
      )}`;
    }

    return `Downgrades to ${seller.pendingPlanChange.plan.title} on ${format(
      new Date(seller.pendingPlanChange.effectiveDate),
      'd MMM, yyyy',
    )}`;
  }

  if (cancelsAt) {
    return `Cancels on ${format(new Date(cancelsAt), 'd MMM, yyyy')}`;
  }

  if (trialEndsAt && isFuture(new Date(trialEndsAt))) {
    return `Trialing, first payment on ${format(new Date(renewsAt), 'd MMM, yyyy')}`;
  }

  return `Renews on ${format(new Date(renewsAt), 'd MMM, yyyy')}`;
}

export function SubscriptionButton({
  plan = {},
  billingPeriod,
  setSelectedPlanHandle,
  upgradePath = '/billing/plan/upgrade',
  upgradeFlow,
  selectedScaleBracket,
}) {
  const { seller, sellerId, plan: sellerPlan, token, setSeller } = useAuth();
  const [error, setError] = useState(null);
  const { emitToast } = useToast();
  const { coupon: couponQuery } = useUrlQuery();

  const pendingPlan = seller.pendingPlanChange?.plan?.handle
    ? getPlan(seller.pendingPlanChange?.plan?.handle, seller.pendingPlanChange?.plan?.version)
    : null;

  const currentPlan = pendingPlan || getPlan(sellerPlan.handle, sellerPlan.version) || {};

  const { data: updatedSeller, refetch: refetchSeller } = useQuery({
    queryKey: sellerKey(sellerId),
    queryFn: () => fetcher(`/sellers/${sellerId}`, token),
  });

  const changePlanMutation = useMutation({
    mutationFn: () =>
      fetcher(`/sellers/${sellerId}/change-subscription`, token, {
        method: 'POST',
        body: JSON.stringify({
          handle: plan.handle,
          billingPeriod,
          coupon: couponQuery,
          upgradeFlow,
          ...(selectedScaleBracket && plan.handle === 'scale' && { bracket: selectedScaleBracket }),
        }),
      }),
    onSuccess: async () => {
      const { data: updatedSeller } = await refetchSeller();

      if (updatedSeller.pendingPlanChange) {
        emitToast({
          title: 'Subscription updated',
          description: `Your subscription will downgrade to ${
            updatedSeller.pendingPlanChange.plan.title
          } on ${format(new Date(updatedSeller.pendingPlanChange.effectiveDate), 'd MMM, yyyy')}`,
          type: 'success',
        });
      } else {
        emitToast({
          title: 'Subscription updated',
          description: `You're now on the ${plan.title} plan`,
          type: 'success',
        });
      }

      setSelectedPlanHandle(null);
    },
    onError: error => {
      setError(error.message);
    },
  });

  useEffect(() => {
    if (!updatedSeller) return;

    setSeller(updatedSeller);
  }, [updatedSeller]);

  useEffect(() => {
    if (!updatedSeller) return;

    // if updatedSeller has a different plan, set selected plan to null
    if (updatedSeller && updatedSeller.plan?.handle !== currentPlan.handle) {
      setSelectedPlanHandle(updatedSeller.plan?.handle);
    }
  }, [currentPlan, updatedSeller]);

  const isLegacyPremium =
    seller.subscriptionStatus === 'legacy' && currentPlan.handle === 'premium';

  const canStartSubscription =
    seller.subscriptionStatus === 'legacy' ||
    seller.subscriptionStatus === 'trialing' ||
    seller.subscriptionStatus === 'canceled' ||
    seller.subscriptionStatus === 'trial_expired' ||
    seller.stripeSubscriptionCancelAt;

  if (plan.handle === 'enterprise' || (plan.handle === 'scale' && selectedScaleBracket > 100000)) {
    return (
      <Button
        appearance="primary"
        size="full"
        onClick={async () => {
          if (!window.$crisp) return;

          window.$crisp.push([
            'set',
            'message:text',
            [`Hi! I want to discuss the Enterprise plan.`],
          ]);

          window.setTimeout(() => {
            window.$crisp.push(['do', 'chat:open']);
          }, 300);
        }}
      >
        Contact us
      </Button>
    );
  }

  if (canStartSubscription) {
    return (
      <Button
        appearance="primary"
        size="full"
        to={`${upgradePath}?${stringify({
          plan: plan.handle,
          billing_period: billingPeriod,
          coupon: couponQuery,
          upgradeFlow,
          ...(selectedScaleBracket &&
            plan.handle === 'scale' && { scaleBracket: selectedScaleBracket }),
        })}`}
      >
        <a className="btn btn-primary">Start subscription</a>
      </Button>
    );
  }

  const hasSubscription = seller.subscriptionStatus === 'active' || isLegacyPremium;

  if (hasSubscription) {
    if (seller.planBillingPeriod !== billingPeriod) {
      const canUpgrade = seller.planBillingPeriod === 'month';

      if (canUpgrade) {
        return (
          <div>
            <FormSubmit.Button
              size="full"
              appearance="primary"
              isSubmitting={changePlanMutation.isLoading}
              hasError={!!error}
              onClick={async () => {
                setError(null);
                changePlanMutation.mutate();
              }}
              disabled={changePlanMutation.isLoading}
            >
              Upgrade to yearly billing
            </FormSubmit.Button>

            {error && (
              <div className="mt-2">
                <FormErrorBase>{error}</FormErrorBase>
              </div>
            )}
          </div>
        );
      }

      return (
        <div className="flex flex-wrap gap-2">
          <Button
            size="full"
            onClick={async () => {
              if (!window.$crisp) return;

              window.$crisp.push([
                'set',
                'message:text',
                [
                  `Hi! I want to change from ${currentPlan.title} - ${getBillingPeriod(
                    seller.planBillingPeriod,
                  )} to ${plan.title} - ${getBillingPeriod(billingPeriod)}.`,
                ],
              ]);

              window.setTimeout(() => {
                window.$crisp.push(['do', 'chat:open']);
              }, 300);
            }}
            appearance="secondary"
          >
            Contact us to change to monthly
          </Button>
        </div>
      );
    } else {
      const isSamePlan = currentPlan.handle === plan.handle;
      const isVersionChange = currentPlan.version !== plan.version;

      let isBracketChange = false;
      if (sellerPlan.handle === 'scale') {
        if (pendingPlan?.handle === 'scale') {
          isBracketChange = selectedScaleBracket !== seller.pendingPlanChange?.plan?.bracket;
        } else {
          isBracketChange = selectedScaleBracket !== sellerPlan?.bracket;
        }
      }

      if (isSamePlan && isVersionChange) {
        return (
          <div>
            <FormSubmit.Button
              size="full"
              appearance="primary"
              isSubmitting={changePlanMutation.isLoading}
              hasError={!!error}
              onClick={async () => {
                setError(null);
                changePlanMutation.mutate();
              }}
              disabled={changePlanMutation.isLoading}
            >
              Change to {plan.title}
            </FormSubmit.Button>

            {error && (
              <div className="mt-2">
                <FormErrorBase>{error}</FormErrorBase>
              </div>
            )}
          </div>
        );
      }

      if (plan.handle === 'scale' && isSamePlan && isBracketChange) {
        return (
          <div>
            <FormSubmit.Button
              size="full"
              appearance="primary"
              isSubmitting={changePlanMutation.isLoading}
              hasError={!!error}
              onClick={async () => {
                setError(null);
                changePlanMutation.mutate();
              }}
              disabled={changePlanMutation.isLoading}
            >
              {!sellerPlan?.bracket || selectedScaleBracket > sellerPlan?.bracket
                ? `Change limit to ${formatPrice({
                    amount: selectedScaleBracket * 100,
                    currency: 'USD',
                  })}`
                : `Change limit to ${formatPrice({
                    amount: selectedScaleBracket * 100,
                    currency: 'USD',
                  })}`}
            </FormSubmit.Button>
          </div>
        );
      }

      const isDowngrade = plan.monthlyPrice < currentPlan.monthlyPrice;

      if (isDowngrade) {
        return (
          <div>
            <FormSubmit.Button
              size="full"
              appearance="secondary"
              isSubmitting={changePlanMutation.isLoading}
              hasError={!!error}
              onClick={async () => {
                setError(null);
                changePlanMutation.mutate();
              }}
              disabled={changePlanMutation.isLoading}
            >
              Downgrade to {plan.title}
            </FormSubmit.Button>

            {error && (
              <div className="mt-2">
                <FormErrorBase>{error}</FormErrorBase>
              </div>
            )}

            <div className="mt-2 text-center text-xs text-gray-500">
              Downgrades take effect at the end of your current billing period
            </div>
          </div>
        );
      }

      if (isSamePlan) {
        return null;
      }

      return (
        <div>
          <FormSubmit.Button
            size="full"
            appearance="primary"
            isSubmitting={changePlanMutation.isLoading}
            hasError={!!error}
            onClick={async () => {
              setError(null);
              changePlanMutation.mutate();
            }}
            disabled={changePlanMutation.isLoading}
          >
            Upgrade to {plan.title}
          </FormSubmit.Button>

          {error && (
            <div className="mt-2">
              <FormErrorBase>{error}</FormErrorBase>
            </div>
          )}
        </div>
      );
    }
  }

  return '';
}

export default PlanOverview;
